import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import LandingPage from "./pages/LandingPage";
import SubmitDetailsPage from "./pages/SubmitDetailsPage";
import BookConsultationPage from "./pages/BookConsultationPage";
import FindMyProgramPage from "./pages/FindMyProgramPage";
import ThankYouPage from "./pages/ThankYouPage";
import PCOSDietPlanPage from "./pages/PCOSDietPlanPage";
import PostpartumRecoveryPage from "./pages/PostpartumRecoveryPage";
import HealthAnalysisPage from "./pages/HealthAnalysisPage";
import PackagesPage from "./pages/PackagesPage";

// Calculators
import CalculatorsIndexPage from "./pages/calculators/CalculatorsIndexPage";
import BMICalculatorPage from "./pages/calculators/BMICalculatorPage";
import WaterIntakeCalculatorPage from "./pages/calculators/WaterIntakeCalculatorPage";
import CalorieDeficitCalculatorPage from "./pages/calculators/CalorieDeficitCalculatorPage";
import ProteinIntakeCalculatorPage from "./pages/calculators/ProteinIntakeCalculatorPage";
import TDEECalculatorPage from "./pages/calculators/TDEECalculatorPage";
import MacroCalculatorPage from "./pages/calculators/MacroCalculatorPage";
import BodyFatCalculatorPage from "./pages/calculators/BodyFatCalculatorPage";
import LeanBodyMassCalculatorPage from "./pages/calculators/LeanBodyMassCalculatorPage";
import IdealWeightCalculatorPage from "./pages/calculators/IdealWeightCalculatorPage";
import FFMICalculatorPage from "./pages/calculators/FFMICalculatorPage";
import WHRCalculatorPage from "./pages/calculators/WHRCalculatorPage";
import HeartRateZoneCalculatorPage from "./pages/calculators/HeartRateZoneCalculatorPage";
import VO2MaxCalculatorPage from "./pages/calculators/VO2MaxCalculatorPage";
import OneRepMaxCalculatorPage from "./pages/calculators/OneRepMaxCalculatorPage";
import PaceCalculatorPage from "./pages/calculators/PaceCalculatorPage";
import PregnancyWeightGainCalculatorPage from "./pages/calculators/PregnancyWeightGainCalculatorPage";
import SleepCalculatorPage from "./pages/calculators/SleepCalculatorPage";
import IntermittentFastingCalculatorPage from "./pages/calculators/IntermittentFastingCalculatorPage";
import CalorieBurnCalculatorPage from "./pages/calculators/CalorieBurnCalculatorPage";
import StepsToCaloriesCalculatorPage from "./pages/calculators/StepsToCaloriesCalculatorPage";
import BMIPrimeCalculatorPage from "./pages/calculators/BMIPrimeCalculatorPage";
import BSACalculatorPage from "./pages/calculators/BSACalculatorPage";
import AdminLoginPage from "./pages/AdminLoginPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import AdminHealthAnalysesPage from "./pages/AdminHealthAnalysesPage";
import AdminEmailLeadsPage from "./pages/AdminEmailLeadsPage";
import AdminTrashPage from "./pages/AdminTrashPage";
import AdminSetupPage from "./pages/AdminSetupPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsPage from "./pages/TermsPage";
import RefundPolicyPage from "./pages/RefundPolicyPage";
import MedicalDisclaimerPage from "./pages/MedicalDisclaimerPage";
import HowWeWorkPage from "./pages/HowWeWorkPage";
import ResourcesPage from "./pages/ResourcesPage";
import ReviewsPage from "./pages/ReviewsPage";
import ContentHubPage from "./pages/ContentHubPage";
import EditorialPolicyPage from "./pages/EditorialPolicyPage";
import DiabetesForParentsPage from "./pages/DiabetesForParentsPage";
import AuthorProfilePage from "./pages/AuthorProfilePage";
import WeightManagementPage from "./pages/WeightManagementPage";
import PrediabetesProgramPage from "./pages/PrediabetesProgramPage";

// SEO Pages
import DoctorLedFitnessPage from "./pages/DoctorLedFitnessPage";
import PregnancyNutritionPage from "./pages/PregnancyNutritionPage";
import FertilityNutritionPage from "./pages/FertilityNutritionPage";
import OnlineFitnessCoachingPage from "./pages/OnlineFitnessCoachingPage";
import MedicalNutritionTherapyPage from "./pages/MedicalNutritionTherapyPage";
import TeamPage from "./pages/TeamPage";
import BlogPage from "./pages/BlogPage";

// Blog Articles
import BlogArticlePregnancyDiet from "./pages/blog/BlogArticlePregnancyDiet";
import BlogArticleFertilityNutrition from "./pages/blog/BlogArticleFertilityNutrition";
import BlogArticlePCOSDiet from "./pages/blog/BlogArticlePCOSDiet";
import BlogArticlePostnatalDiet from "./pages/blog/BlogArticlePostnatalDiet";
import BlogArticleWeekendSports from "./pages/blog/BlogArticleWeekendSports";
import BlogArticleProteinComparison from "./pages/blog/BlogArticleProteinComparison";
import BlogArticleProteinPowder from "./pages/blog/BlogArticleProteinPowder";
import BlogArticleCookingOil from "./pages/blog/BlogArticleCookingOil";
import BlogArticleDynamic from "./pages/blog/BlogArticleDynamic";

const SITE_URL = "https://tcubefitness.com";
const NO_CANONICAL_PATHS = ["/admin", "/thank-you", "/submit-details"];

const CanonicalLink = () => {
  const { pathname } = useLocation();

  React.useEffect(() => {
    const links = document.querySelectorAll('link[rel="canonical"]');
    if (NO_CANONICAL_PATHS.some((path) => pathname.startsWith(path))) {
      links.forEach((link) => link.remove());
      return;
    }

    const link = links[0] || document.createElement("link");
    links.forEach((extra, index) => {
      if (index > 0) extra.remove();
    });
    link.setAttribute("rel", "canonical");
    link.setAttribute("href", `${SITE_URL}${pathname}`);
    if (!links[0]) document.head.appendChild(link);
  }, [pathname]);

  return null;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <CanonicalLink />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/submit-details" element={<SubmitDetailsPage />} />
          <Route path="/book-consultation" element={<BookConsultationPage />} />
          <Route path="/find-my-program" element={<FindMyProgramPage />} />
          <Route path="/thank-you" element={<ThankYouPage />} />
          <Route path="/pcos-diet-plan" element={<PCOSDietPlanPage />} />
          <Route path="/postpartum-recovery" element={<PostpartumRecoveryPage />} />
          <Route path="/health-analysis" element={<HealthAnalysisPage />} />

          {/* Calculators */}
          <Route path="/calculators" element={<CalculatorsIndexPage />} />
          <Route path="/calculators/bmi" element={<BMICalculatorPage />} />
          <Route path="/calculators/water-intake" element={<WaterIntakeCalculatorPage />} />
          <Route path="/calculators/calorie-deficit" element={<CalorieDeficitCalculatorPage />} />
          <Route path="/calculators/protein-intake" element={<ProteinIntakeCalculatorPage />} />
          <Route path="/calculators/tdee" element={<TDEECalculatorPage />} />
          <Route path="/calculators/macro" element={<MacroCalculatorPage />} />
          <Route path="/calculators/body-fat" element={<BodyFatCalculatorPage />} />
          <Route path="/calculators/lean-body-mass" element={<LeanBodyMassCalculatorPage />} />
          <Route path="/calculators/ideal-weight" element={<IdealWeightCalculatorPage />} />
          <Route path="/calculators/ffmi" element={<FFMICalculatorPage />} />
          <Route path="/calculators/waist-to-hip" element={<WHRCalculatorPage />} />
          <Route path="/calculators/heart-rate-zones" element={<HeartRateZoneCalculatorPage />} />
          <Route path="/calculators/vo2-max" element={<VO2MaxCalculatorPage />} />
          <Route path="/calculators/one-rep-max" element={<OneRepMaxCalculatorPage />} />
          <Route path="/calculators/pace" element={<PaceCalculatorPage />} />
          <Route path="/calculators/pregnancy-weight-gain" element={<PregnancyWeightGainCalculatorPage />} />
          <Route path="/calculators/sleep" element={<SleepCalculatorPage />} />
          <Route path="/calculators/intermittent-fasting" element={<IntermittentFastingCalculatorPage />} />
          <Route path="/calculators/calorie-burn" element={<CalorieBurnCalculatorPage />} />
          <Route path="/calculators/steps-to-calories" element={<StepsToCaloriesCalculatorPage />} />
          <Route path="/calculators/bmi-prime" element={<BMIPrimeCalculatorPage />} />
          <Route path="/calculators/bsa" element={<BSACalculatorPage />} />

          <Route path="/packages" element={<PackagesPage />} />
          <Route path="/admin" element={<AdminLoginPage />} />
          <Route path="/admin/setup" element={<AdminSetupPage />} />
          <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
          <Route path="/admin/health-analyses" element={<AdminHealthAnalysesPage />} />
          <Route path="/admin/email-leads" element={<AdminEmailLeadsPage />} />
          <Route path="/admin/trash" element={<AdminTrashPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/refund-policy" element={<RefundPolicyPage />} />
          <Route path="/medical-disclaimer" element={<MedicalDisclaimerPage />} />
          <Route path="/how-we-work" element={<HowWeWorkPage />} />
          <Route path="/resources" element={<ResourcesPage />} />
          <Route path="/reviews" element={<ReviewsPage />} />
          <Route path="/hub/:hubSlug" element={<ContentHubPage />} />
          <Route path="/editorial-policy" element={<EditorialPolicyPage />} />
          <Route path="/diabetes-diet-for-parents" element={<DiabetesForParentsPage />} />
          <Route path="/diabetes-for-parents" element={<Navigate to="/diabetes-diet-for-parents" replace />} />
          <Route path="/author/:slug" element={<AuthorProfilePage />} />
          <Route path="/doctor-led-weight-management" element={<WeightManagementPage />} />
          <Route path="/prediabetes-program" element={<PrediabetesProgramPage />} />

          {/* SEO Pages */}
          <Route path="/doctor-led-fitness" element={<DoctorLedFitnessPage />} />
          <Route path="/pregnancy-nutrition" element={<PregnancyNutritionPage />} />
          <Route path="/fertility-nutrition" element={<FertilityNutritionPage />} />
          <Route path="/online-fitness-coaching" element={<OnlineFitnessCoachingPage />} />
          <Route path="/medical-nutrition-therapy" element={<MedicalNutritionTherapyPage />} />
          <Route path="/our-team" element={<TeamPage />} />

          {/* Blog */}
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/best-diet-plan-for-pregnancy" element={<BlogArticlePregnancyDiet />} />
          <Route path="/blog/fertility-nutrition-hormone-balance" element={<BlogArticleFertilityNutrition />} />
          <Route path="/blog/pcos-diet-plan-complete-guide" element={<BlogArticlePCOSDiet />} />
          <Route path="/blog/best-diet-plan-after-delivery" element={<BlogArticlePostnatalDiet />} />
          <Route path="/blog/weekend-sports-nutrition-warmup-guide" element={<BlogArticleWeekendSports />} />
          <Route path="/blog/prawns-fish-chicken-protein-comparison" element={<BlogArticleProteinComparison />} />
          <Route path="/blog/types-of-protein-powder-comparison" element={<BlogArticleProteinPowder />} />
          <Route path="/blog/best-cooking-oil-indian-cooking-guide" element={<BlogArticleCookingOil />} />
          <Route path="/blog/:slug" element={<BlogArticleDynamic />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
